import type { User } from '@axinom/mosaic-core';
import {
  Button,
  ButtonContext,
  IconName,
  Icons,
  SvgElement,
  useExpand,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import React from 'react';
import { Link } from 'react-router-dom';
import type { ExtensionSlotType } from '../ExtensionSlotType';
import classes from './Auth.module.scss';

export interface AuthProps {
  user?: User;
  logout?: () => unknown;
  className?: string;
  ExtensionSlot?: ExtensionSlotType;
  /** Path to navigate to when the "Profile Settings" menu item is clicked. If not set, the menu item is hidden. */
  profileSettingsPath?: string;
}

/**
 * Displays user
 */
export const Auth: React.FC<AuthProps> = ({
  user,
  logout,
  className,
  ExtensionSlot,
  profileSettingsPath,
}) => {
  const { isExpanded, expand, collapse } = useExpand();

  return (
    <div
      className={clsx(classes.container, 'auth-container', className)}
      onMouseDown={expand}
      onMouseLeave={collapse}
      data-test-id="header-auth"
    >
      <div
        className={clsx(classes.profileContainer)}
        title={`${user?.name} (${user?.email ?? ''})`}
      >
        {user?.name && (
          <div className={classes.profilePicture}>{getInitials(user.name)}</div>
        )}
        <span className={clsx(classes.userName)}>{user?.name}</span>
        <Button
          icon={IconName.ChevronDown}
          className={classes.button}
          buttonContext={ButtonContext.None}
        />
        {isExpanded && (
          <div className={clsx(classes.menu)}>
            {profileSettingsPath && (
              <Link
                className={clsx(classes.logoutBtn)}
                to={profileSettingsPath}
                data-test-id="header-profile-settings"
              >
                Profile Settings
                <Icons icon={IconName.ChevronRight} />
              </Link>
            )}
            <button className={clsx(classes.logoutBtn)} onClick={logout}>
              Logout
              <SvgElement viewBox="0 0 40 40">
                <path
                  vectorEffect="non-scaling-stroke"
                  fill="none"
                  strokeWidth="2"
                  d="M26,37.9H2V2.1h24 M28.6,12.2l9.4,7.8l-9.4,8.6 M38,20.1H14.5"
                ></path>
              </SvgElement>
            </button>
          </div>
        )}
      </div>
      <div className={clsx(classes.logoContainer)}>
        <div className={clsx(classes.logo)}>
          {ExtensionSlot ? (
            <ExtensionSlot
              name="logo"
              empty={() => mosaicLogo}
              render={(children) => children[0] as React.ReactElement}
            />
          ) : (
            mosaicLogo
          )}
        </div>
      </div>
    </div>
  );
};

function getInitials(name: string): string {
  const words = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) {
    return '';
  }
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

const mosaicLogo = (
  <SvgElement viewBox="0 0 270 90" title="Mosaic Logo" aria-label="Mosaic Logo">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 270 90">
      <path
        fill="#00467d"
        d="M35.2,16.3c-8.4,4.4-12.5,6.5-14.8,10.3-2.3,3.8-2.3,8.5-2.3,17.9v1c0,9.4,0,14.1,2.3,17.9,2.3,3.8,6.5,6,14.8,10.3l6,3.1c7.2,3.8,10.9,5.7,14.8,5.7s7.6-1.9,14.8-5.7l6-3.1c8.4-4.4,12.5-6.5,14.8-10.3,2.3-3.8,2.3-8.5,2.3-17.9v-1c0-9.4,0-14.1-2.3-17.9s-6.5-6-14.8-10.3l-6-3.1c-7.2-3.8-10.9-5.7-14.8-5.7s-7.6,1.9-14.8,5.7c0,0-6,3.1-6,3.1Z"
      />
      <path
        fill="#28aae1"
        d="M33.3,31.2c.4-2.4,2.8-4,5.2-3.6,2.4.4,4,2.8,3.6,5.2l-4.4,24.1c-.4,2.4-2.8,4-5.2,3.6-2.4-.4-4-2.8-3.6-5.2,0,0,4.4-24.1,4.4-24.1Z"
      />
      <path
        fill="#ffc81e"
        d="M35,28.4c2-1.5,4.8-1.1,6.2.9l18.3,24.1c1.5,2,1.1,4.8-.9,6.2-2,1.5-4.8,1.1-6.2-.9l-18.3-24.1c-1.5-2-1.1-4.8.9-6.2Z"
      />
      <path
        fill="#28aae1"
        d="M73.1,27.6c2.4-.4,4.7,1.2,5.2,3.6l4.4,24.1c.4,2.4-1.2,4.7-3.6,5.2-2.4.4-4.7-1.2-5.2-3.6l-4.4-24.1c-.4-2.4,1.2-4.7,3.6-5.2Z"
      />
      <path
        fill="#f06e55"
        d="M70.3,29.3c1.5-2,4.3-2.4,6.2-.9s2.4,4.3.9,6.2l-17.9,24.1c-1.5,2-4.3,2.4-6.2.9s-2.4-4.3-.9-6.2c0,0,17.9-24.1,17.9-24.1Z"
      />
      <path
        fill="#00467d"
        d="M253.6,36.8c0,1.5-1,2.8-2.5,2.8s-2.8-1-2.8-2.5l-.8-9.7-7.5,10.6c-.2.4-.5.8-.9,1.1-.2.2-.4.3-.7.3,0,0,0,0-.1,0,0,0-.2,0-.2,0-.7.2-1.5,0-2.2-.4-.5-.3-.8-.8-1-1.2l-7.3-10.3-.7,9.5c0,1.5-1.4,2.6-2.8,2.5-1.5,0-2.6-1.4-2.5-2.8,0,0,1.2-15.7,1.3-16.5,0-.5,0-1,.3-1.4,0,0,0,0,0-.1,0,0,0-.1.1-.2,0-.1.1-.2.2-.3,0,0,0,0,0,0,0-.1.2-.2.3-.3,0,0,0,0,0,0,0,0,0,0,0,0,.1,0,.3-.2.4-.2,0,0,0,0,0,0,.4-.2.8-.3,1.3-.3,1,0,1.8.7,2.2,1.5l9.6,13.5,9.9-13.9h0c.8-1.2,2.3-1.5,3.5-.7,0,0,0,0,.1,0,0,0,0,0,0,0,0,0,0,0,0,0,.4.3.7.7.9,1.2,0,0,0,0,0,0,0,.1,0,.3.1.4,0,0,0,0,0,0,0,0,0,.2,0,.3l1.3,17ZM137.2,50.3c0,0-.2,0-.3,0,0,0,0,0,0,0-.1,0-.3,0-.4.1,0,0,0,0,0,0-.5.2-.9.5-1.2.9,0,0,0,0,0,0,0,0-6.9,9.7-9.9,13.9l-9.6-13.5c-.4-.8-1.2-1.5-2.2-1.5-.5,0-.9,0-1.3.3,0,0,0,0,0,0-.1,0-.3.1-.4.2,0,0,0,0,0,0,0,0,0,0,0,0-.1,0-.2.2-.3.3,0,0,0,0,0,0,0,0-.2.2-.2.3,0,0,0,.1-.1.2,0,0,0,0,0,.1-.2.4-.3.9-.3,1.4l-1.3,16.5c0,1.4,1,2.8,2.5,2.8,1.4,0,2.8-1,2.8-2.5l.7-9.5,7.3,10.3c.2.5.5.9,1,1.2.7.5,1.4.6,2.2.4,0,0,.2,0,.2,0,0,0,0,0,.1,0,.2,0,.5-.2.7-.3.4-.3.7-.7.9-1.1l7.6-10.6.8,9.6c0,1.5,1.3,2.6,2.8,2.5,1.5,0,2.6-1.3,2.5-2.8l-1.3-17c0-1.5-1.3-2.6-2.8-2.5ZM111,35.9l8.5-17v-.2c.5-.8,1.4-1.3,2.4-1.3h0c1,0,2,.4,2.4,1.4l8.5,17c.6,1.3,0,2.9-1.2,3.6-1.3.6-2.9,0-3.6-1.2l-1-2c-.2,0-.3,0-.5,0h-9.7l-1,2c-.6,1.3-2.3,1.9-3.6,1.2-1.2-.6-1.9-2.3-1.2-3.6ZM119.4,30.9h4.9l-2.5-4.9-2.4,4.9ZM137,38.9c1.2,1,2.8.9,3.8-.2l5.8-6.3,5.8,6.3c1.1,1.1,2.8,1.2,3.8.2,1.1-1.1,1.2-2.8.2-3.8l-6.1-6.7,6.1-6.7c1-1.2.9-2.8-.2-3.8-1.2-1-2.8-.9-3.8.2,0,0-3.1,3.4-5.8,6.3l-5.8-6.3c-1.1-1.1-2.8-1.2-3.8-.2-1.1,1.1-1.2,2.8-.2,3.8l6.1,6.7c-2.9,3.2-6.1,6.7-6.1,6.7-1,1.2-.9,2.8.2,3.8ZM164.2,39.7c1.5,0,2.7-1.2,2.7-2.7v-17c0-1.5-1.2-2.7-2.7-2.7s-2.7,1.2-2.7,2.7v17c0,1.5,1.2,2.7,2.7,2.7ZM222.7,50.3c-1.5,0-2.7,1.2-2.7,2.7v17c0,1.5,1.2,2.7,2.7,2.7s2.7-1.2,2.7-2.7v-17c0-1.5-1.2-2.7-2.7-2.7ZM189.3,17.4c-1.5,0-2.7,1.2-2.7,2.7v9.6l-9.6-11.4c-.7-.8-1.9-1.2-2.9-.8-1.1.4-1.8,1.3-1.8,2.5v17c0,1.5,1.2,2.7,2.7,2.7s2.7-1.2,2.7-2.7v-9.6l9.6,11.4c.7.8,1.9,1.2,2.9.8,1.1-.4,1.8-1.3,1.8-2.5v-17c0-1.5-1.2-2.7-2.7-2.7ZM219.2,28.4c0,6.4-5.2,11.7-11.7,11.7s-11.7-5.2-11.7-11.7,5.2-11.7,11.7-11.7,11.7,5.2,11.7,11.7ZM213.8,28.4c0-3.5-2.8-6.2-6.2-6.2s-6.2,2.8-6.2,6.2,2.8,6.2,6.2,6.2,6.2-2.8,6.2-6.2ZM168.3,61.5c0,6.4-5.2,11.7-11.7,11.7s-11.7-5.2-11.7-11.7,5.2-11.7,11.7-11.7,11.7,5.2,11.7,11.7ZM162.9,61.5c0-3.5-2.8-6.2-6.2-6.2s-6.2,2.8-6.2,6.2,2.8,6.2,6.2,6.2,6.2-2.8,6.2-6.2ZM241.7,55.2c1.5,0,2.9.5,4,1.4,1.1,1,2.8.8,3.7-.4,1-1.1.8-2.8-.4-3.7-2-1.7-4.6-2.7-7.5-2.7-6.4,0-11.6,5.2-11.7,11.6,0,6.5,5.3,11.6,11.7,11.6s5.4-1,7.5-2.7c1.2-.9,1.3-2.6.4-3.7-.9-1.2-2.6-1.3-3.7-.4-1.1.9-.5,1.4-4,1.4s-6.3-2.8-6.3-6.2,2.8-6.2,6.3-6.2ZM229.9,61.5s0,0,0,0c0,0,0,0,0,0v.2ZM182.7,58.7c-1.2-.2-2-.3-2.8-.4-.8-.2-1.2-.4-1.3-.5-.3-.2-.3-.4-.3-.4,0,0,0-.3,0-.5.2-.4.8-1.1,1.9-1.2,3.1-.4,4.6.4,4.8.4,1.3.6,2.9.2,3.6-1.2.6-1.3.2-2.9-1.2-3.6-.9-.5-3.6-1.8-8.2-1h0c-2.8.5-5,2.2-6,4.5-1.1,2.3-.6,5.2,1.7,7.1,1.2,1,2.5,1.4,3.6,1.7s2.4.4,3.3.5c2.2.4,2.8.7,3.1,1.2.2.4.2.5.2.7s0,.4-.4.7c-.6.6-1.7,1.2-3.7,1.2s-3.3-.4-3.9-.8c-.4-.2-.5-.4-.6-.4-1-1.1-2.6-1.2-3.7-.4s-1.3,2.6-.4,3.7c0,0,.2.3.2.3l.4.4c.4.3.9.7,1.6,1.1,1.4.8,3.4,1.4,6.4,1.4s5.9-1,7.7-2.9c1.9-2,2.5-5.1.9-7.8-1.7-2.9-5.1-3.5-6.9-3.7ZM215.8,68.8c.6,1.3,0,2.9-1.2,3.6-1.3.6-2.9,0-3.6-1.2l-1-1.9c-.1,0-.3,0-.5,0h-9.8l-.9,1.9c-.6,1.3-2.3,1.9-3.6,1.2-1.3-.6-1.9-2.3-1.2-3.6l8.5-17v-.2c.5-.8,1.4-1.3,2.4-1.3h0c1,0,2,.4,2.4,1.4l8.5,17ZM207.4,64l-2.5-5-2.5,5h5Z"
      />
    </svg>
  </SvgElement>
);
